import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { Keyboard, Mousewheel } from 'swiper'
import 'swiper/css'
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react'
import { isDesktop, isNativeApp } from '../../../client'
import { usePage } from '../../../contexts'
import { ErrorBoundary } from '../../../sharedComponents'
import { sendGA4 } from '../../../utils'
import { PlayerContext } from '../../Player/Store/PlayerContext'
import { ArrowInCircleIcon } from './icons'
import { usePlayer } from './PlayerContext'
import {
  PlayerViewContainer,
  PlayerViewSwiperButton,
  PlayerViewSwiperButtonContainer,
  PlayerViewSwiperPlaceHolder,
  PlayerViewWrapper,
} from './PlayerView.style'
import { IPlayerViewProps, IReelSlide, IVideoRefAnalytics } from './type'
import { VastPlayer } from './VastPlayer/VastPlayer'
import dynamic from 'next/dynamic'
import { buildReelSlides } from './utils'
import { readNetwork } from './network'
import { useModalDialog } from './focusTrap'
import { reelsHe } from '../../../locales/reels.he'
/**
 * The player's code. The feed calls it once its cards come near the screen,
 * so a tap does not wait for the download (the import is shared, it loads once).
 */
export const loadPlayer = () => import('./Player/Player')
const Player = dynamic(() => loadPlayer().then(p => p.Player), {
  ssr: false,
})

/** a tap that moves a few px is still a tap (Swiper's 0 swallowed clicks on the buttons) */
const SWIPE_THRESHOLD_PX = 8

export const PlayerView = ({
  showPlayer,
  closePlayer,
  updatedPost,
  stagger,
  loop = true,
  seekBarColor = '#fff',
  playerStyle = {
    borderCorner: 'square',
    seekBar: 'linear',
    overlayPadding: 20,
    overlayPaddingMobile: 12,
    seekBarTimeSize: 16,
    overlayInfoPaddingBottom: 30,
    overlayInfoPaddingBottomMobile: 16,
    overlayInfoGap: 14,
  },
}: IPlayerViewProps) => {
  const [reels, setReels] = useState<IReelSlide[] | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  // the last slide that started playing. The window around the active slide
  // (`behind` back, `ahead` forward) loads only once it plays, so it never competes
  // with its start; until then the window of the slide that played before
  // stays loaded, so a quick swipe back or forward lands on a ready clip
  const [{ ahead, behind, preloadAdMedia }] = useState(readNetwork)
  const [startedIndex, markStarted] = useState<number | null>(null)
  const inWindow = (index: number, center: number) =>
    ahead > 0 && index - center >= -behind && index - center <= ahead
  const keepsNeighbour = (index: number) =>
    inWindow(index, activeIndex) &&
    startedIndex !== null &&
    (startedIndex === activeIndex || inWindow(index, startedIndex))
  // slides being swiped away keep their (paused) player until the transition
  // ends, so it does not snap back to its poster mid-swipe
  const [leaving, setLeaving] = useState<number[]>([])
  // the loop round, part of every slide key (the same clips are appended again)
  const roundRef = useRef(0)
  const [canNavForward, setCanNavForward] = useState<boolean>(true)
  const [canNavBack, setCanNavBack] = useState<boolean>(false)

  // Analytics
  const sessionCount = useRef<number>(0)
  const currentVideo = useRef<IVideoRefAnalytics>({
    video: null,
    title: '',
    index: -1,
  })

  // Refs
  const nextVideo = useRef<IVideoRefAnalytics>({
    video: null,
    title: '',
    index: -1,
  })
  const pageData = usePage()

  // Reference
  const containerRef = useRef<HTMLDivElement>(null)
  const swiperRef = useRef<SwiperType | null>(null)
  const touchReleaseTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)

  const playerCtx = useContext(PlayerContext)
  const { resetMute, isMiniSeries, adsInterval } = usePlayer()
  const { setVideoPlaying } = playerCtx

  const syncNavState = useCallback((swiper: SwiperType) => {
    setCanNavBack(!swiper.isBeginning)
    setCanNavForward(!swiper.isEnd)
  }, [])

  const scheduleTouchReleaseOnEdges = (
    swiper: SwiperType,
    value: boolean,
    delay = 0
  ) => {
    if (touchReleaseTimeoutRef.current) {
      clearTimeout(touchReleaseTimeoutRef.current)
    }
    touchReleaseTimeoutRef.current = setTimeout(() => {
      if (swiper.destroyed || !swiper.params) {
        return
      }
      swiper.params.touchReleaseOnEdges = value
    }, delay)
  }

  // on mount, on unmount
  useEffect(() => {
    isMountedRef.current = true
    setReels(buildReelSlides(updatedPost, adsInterval))

    return () => {
      isMountedRef.current = false
      if (touchReleaseTimeoutRef.current) {
        clearTimeout(touchReleaseTimeoutRef.current)
      }

      sendGA4({
        eventName: isNativeApp()
          ? 'app_vertical_next_video_loader'
          : 'vertical_next_video_loader',
        pageData: pageData,
        num: sessionCount.current,
        current_video_title: currentVideo.current.title,
        current_reference_id: currentVideo.current.video?.name,
        next_video_title: nextVideo.current.title,
      })
      resetMute()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setVideoPlaying(!!showPlayer)
  }, [setVideoPlaying, showPlayer])

  // Add this effect to update refs when active index changes
  useEffect(() => {
    if (reels && reels.length > 0) {
      const current = reels[activeIndex]

      let nextVideoItem = null
      let nextVideoIndex = activeIndex
      for (let i = 1; i <= reels.length; i++) {
        const checkIndex = (activeIndex + i) % reels.length

        // If we've looped all the way back to the current index, stop
        if (checkIndex === activeIndex && i > 1) {
          break
        }

        if (reels[checkIndex]?.type === 'video') {
          nextVideoItem = reels[checkIndex]
          nextVideoIndex = checkIndex
          break // Found it, stop looking
        }
      }

      currentVideo.current = {
        video: current?.type === 'video' ? current.item.video : null,
        title: current?.type === 'video' ? current.item.title : '',
        index: activeIndex,
      }
      if (nextVideoItem && nextVideoItem.type === 'video') {
        nextVideo.current = {
          video: nextVideoItem.item.video,
          title: nextVideoItem.item.title,
          index: nextVideoIndex,
        }
      }
    }
  }, [activeIndex, reels])

  const handleAdError = () => {
    const swiper = swiperRef.current
    if (!swiper) {
      return
    }

    const wasGoingForward = swiper.activeIndex >= swiper.previousIndex
    if (wasGoingForward) {
      swiper.slideNext()
    } else {
      swiper.slidePrev()
    }
  }

  // ad slides that already played or failed while on screen; they stay in the
  // list (removing them would shift every later slide and drop the clip loaded
  // ahead) and are skipped if the user comes back to them
  const doneAdsRef = useRef<Set<number>>(new Set())
  const [doneAds, setDoneAds] = useState<number[]>([])

  const handleAdFinished = useCallback(
    (slideIndex: number) => {
      const swiper = swiperRef.current

      // Determine direction *before* mutating reels
      const cameFromPrev = swiper && swiper.previousIndex < slideIndex

      if (swiper && slideIndex <= swiper.activeIndex) {
        doneAdsRef.current.add(slideIndex)
        setDoneAds(Array.from(doneAdsRef.current))
        if (slideIndex === swiper.activeIndex) {
          if (cameFromPrev) {
            swiper.slideNext()
          } else {
            swiper.slidePrev()
          }
        }
        return
      }

      // an ad that failed before the user reached it sits after the active
      // slide, so removing it does not move what is on screen
      setReels(prev => {
        if (!prev) {
          return prev
        }
        return prev.filter((_, i) => i !== slideIndex)
      })
      requestAnimationFrame(() => {
        const swiper = swiperRef.current
        if (!swiper) {
          return
        }
        swiper.update()
        syncNavState(swiper)

        // If the user came from the previous slide (scrolled forward into the ad),
        // after removal the index stays correct and shows the next video.
        // If the user came from the next slide (scrolled back into the ad),
        // we need to go one step back to show the slide before the ad.
        if (!cameFromPrev) {
          swiper.slideTo(slideIndex - 1, 0) // 0 = no animation
        }
      })
    },
    [syncNavState]
  )

  const preventTouchLeaks = (e: React.TouchEvent<HTMLDivElement>) => {
    // e.preventDefault()
    e.stopPropagation()
  }

  const handleClosePlayer = () => {
    let index = activeIndex + 1 + stagger
    if (isMiniSeries) {
      index = activeIndex - Math.floor(activeIndex / (adsInterval + 1))
    } else {
      // the card after the last clip watched (an ad on screen counts as the
      // clip before it): by the clip's own index, so the ad slides and loop
      // rounds in the feed do not push the cards row too far
      const lastClip = reels
        ?.slice(0, activeIndex + 1)
        .reverse()
        .find(slide => slide.type === 'video')
      const original =
        lastClip?.type === 'video' ? lastClip.item.originalIndex : undefined
      if (typeof original === 'number') {
        index = original + 1
      }
    }
    closePlayer(index, currentVideo.current.video?.kalturaId)
  }

  const handleSwiperInit = useCallback(
    (swiper: SwiperType) => {
      swiperRef.current = swiper
      // initialSlide does not fire slideChange; later changes all do
      setActiveIndex(swiper.activeIndex)
      syncNavState(swiper)
    },
    [syncNavState]
  )

  const swiperProps = useMemo<SwiperProps>(() => {
    const desktop = isDesktop()
    return {
      modules: desktop ? [Keyboard, Mousewheel] : [Keyboard],
      mousewheel: desktop,
      keyboard: { enabled: true },
      // touch on every touch screen (an iPad in landscape counts as desktop);
      // a mouse drag does not move slides, the wheel, arrows and keys do
      allowTouchMove: true,
      simulateTouch: false,
      threshold: SWIPE_THRESHOLD_PX,
      preloadImages: true,
      watchSlidesProgress: true,
      slidesPerView: 1,
      spaceBetween: 20,
      initialSlide: isMiniSeries
        ? stagger + Math.floor(stagger / adsInterval)
        : 0,
      direction: 'vertical',
      onSwiper: handleSwiperInit,
    }
  }, [adsInterval, isMiniSeries, stagger, handleSwiperInit])

  // keyboard: focus in, Escape closes, Tab stays inside, focus back to the card
  const onDialogKeyDown = useModalDialog(containerRef, handleClosePlayer)

  return (
    <PlayerViewContainer
      showPlayer={showPlayer}
      ref={containerRef}
      role='dialog'
      aria-modal='true'
      aria-label={reelsHe.dialog}
      tabIndex={-1}
      onKeyDown={onDialogKeyDown}
      onTouchStart={preventTouchLeaks}
      onTouchEnd={preventTouchLeaks}
      onScroll={preventTouchLeaks}
    >
      <PlayerViewWrapper isMiniSeries={isMiniSeries}>
        {reels && reels.length > 0 ? (
          <Swiper
            {...swiperProps}
            onSlideChangeTransitionEnd={() => setLeaving([])}
            onSlideChange={swiper => {
              if (isMountedRef.current) {
                // React 17 does not batch native callbacks: mark the leaving
                // slide first, or one render has it neither active nor
                // leaving and its player is destroyed mid-swipe
                setLeaving(prev => [...prev, swiper.previousIndex])
                setActiveIndex(swiper.activeIndex)
                syncNavState(swiper)
              }
              scheduleTouchReleaseOnEdges(swiper, false)
              if (doneAdsRef.current.has(swiper.activeIndex)) {
                const forward = swiper.activeIndex > swiper.previousIndex
                requestAnimationFrame(() =>
                  forward ? swiper.slideNext() : swiper.slidePrev()
                )
              }
            }}
            onSlideNextTransitionEnd={() => {
              sessionCount.current += 1
            }}
            onUpdate={syncNavState}
            onReachEnd={swiper => {
              scheduleTouchReleaseOnEdges(swiper, true)
              if (loop && isMountedRef.current) {
                setReels(
                  prev =>
                    prev && [
                      ...prev,
                      ...buildReelSlides(
                        updatedPost,
                        adsInterval,
                        ++roundRef.current
                      ),
                    ]
                )
                requestAnimationFrame(() => {
                  const s = swiperRef.current
                  if (s) {
                    s.update()
                    syncNavState(s)
                  }
                })
              }
            }}
          >
            {reels &&
              reels.map((slide, index) => (
                <SwiperSlide
                  key={slide.key}
                  aria-hidden={index !== activeIndex}
                  className={slide.type === 'ad' ? 'ad' : 've'}
                >
                  {({ isVisible }) => {
                    const isActive = index === activeIndex
                    return (
                      <ErrorBoundary>
                        {slide.type === 'ad' ? (
                          <VastPlayer
                            width='auto'
                            height='100%'
                            isActive={isActive && !doneAds.includes(index)}
                            onClose={handleClosePlayer}
                            onFinished={() => handleAdFinished(index)}
                            onError={handleAdError}
                            preFetch={
                              index > activeIndex &&
                              keepsNeighbour(index) &&
                              !doneAds.includes(index)
                            }
                            // the whole ad file only on screen or next; one
                            // further ahead gets its metadata
                            preloadMedia={
                              preloadAdMedia && index <= activeIndex + 1
                            }
                            onPlaying={() => markStarted(index)}
                          />
                        ) : (
                          <Player
                            slidedData={{
                              video: slide.item.video,
                              episode: slide.item.episode,
                              img: slide.item.poster,
                              title: slide.item.title,
                              link: slide.item.link,
                              link_title: slide.item.link_title,
                              realIndex: slide.item.originalIndex || index,
                              timelineItemTime: slide.item.time,
                              timelineItemDay: slide.item.day,
                            }}
                            index={index}
                            slideKey={slide.key}
                            isActive={isActive}
                            shouldLoad={
                              isActive ||
                              leaving.includes(index) ||
                              keepsNeighbour(index)
                            }
                            onPlaying={markStarted}
                            isVisible={isVisible}
                            // the neighbours' images wait for the active clip
                            // to start, like their players
                            showPoster={
                              isActive ||
                              (startedIndex !== null &&
                                Math.abs(index - activeIndex) <= 2)
                            }
                            handleClosePlayer={handleClosePlayer}
                            seekBarColor={seekBarColor}
                            playerStyle={playerStyle}
                            isMiniSeries={isMiniSeries}
                          />
                        )}
                      </ErrorBoundary>
                    )
                  }}
                </SwiperSlide>
              ))}
          </Swiper>
        ) : (
          <PlayerViewSwiperPlaceHolder />
        )}
        <PlayerViewSwiperButtonContainer>
          <PlayerViewSwiperButton
            type='button'
            aria-label={reelsHe.previous}
            reverse={true}
            onClick={() => {
              swiperRef.current?.slidePrev()
            }}
            enabled={canNavBack}
          >
            <ArrowInCircleIcon />
          </PlayerViewSwiperButton>
          <PlayerViewSwiperButton
            type='button'
            aria-label={reelsHe.next}
            onClick={() => {
              swiperRef.current?.slideNext()
            }}
            enabled={canNavForward}
          >
            <ArrowInCircleIcon direction={'to-bottom'} />
          </PlayerViewSwiperButton>
        </PlayerViewSwiperButtonContainer>
      </PlayerViewWrapper>
    </PlayerViewContainer>
  )
}
